import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAppSelector } from "../hooks/redux.js";
import { hasDetailPanelOpen } from "../utils/selectors.js";
import { useViewportSize } from "@mantine/hooks";
import { useChatOpen } from "../hooks/assistant.js";
import { useStore } from "react-redux";
import { getAIChatOpen } from "../reducers/chatReducer.js";
const medianWidth = 16;
const medianWidthPx = "16px";
const minimumMapWidth = 240;
const chatToPanelGap = 16;
const getLeftSideBarWidth = (windowWidth, panelWidth) => {
    const previouslySavedWidth = localStorage.getItem("mapka-leftPanelWidth");
    if (previouslySavedWidth) {
        const previous = parseInt(previouslySavedWidth);
        if ((previous + panelWidth) < (windowWidth - minimumMapWidth)) {
            return panelWidth
                ? previous + panelWidth + chatToPanelGap
                : previous;
        }
        else if ((previous) < (windowWidth - minimumMapWidth)) {
            return previous;
        }
    }
    if (windowWidth > 3300) {
        return 1400 + panelWidth;
    }
    if (windowWidth > 2400) {
        return 1000 + panelWidth;
    }
    return 540 + panelWidth;
};
const getRightPanelWidth = (width) => {
    return width < 576 ? "0px" : "1fr";
};
const usePanelWidth = (view) => {
    const detailPanelOpen = useAppSelector(hasDetailPanelOpen);
    if (detailPanelOpen)
        return 640;
    if (view === "ai")
        return 0;
    if (view === "versions")
        return 640;
    return 320;
};
export function SplitPanel({ rightPanel, leftPanel, view }) {
    const splitPanelRef = useRef(null);
    const medianRef = useRef(null);
    const resizing = useRef(false);
    const store = useStore();
    const panelWidth = usePanelWidth(view);
    const isChatOpen = useChatOpen();
    const { width } = useViewportSize();
    const [leftPanelWidth, setLeftPanelWidth] = useState(() => getLeftSideBarWidth(window.innerWidth, panelWidth));
    const [rightPanelWidth, setRightPanelWidth] = useState(() => getRightPanelWidth(window.innerWidth));
    useLayoutEffect(() => {
        if (width === 0) {
            return;
        }
        if (width < 576) {
            setLeftPanelWidth(width);
            setRightPanelWidth("0px");
        }
        if (!isChatOpen) {
            setLeftPanelWidth(panelWidth);
            return;
        }
        setLeftPanelWidth(getLeftSideBarWidth(width, panelWidth));
    }, [width, panelWidth, isChatOpen]);
    useEffect(() => {
        const handlePointerDown = (event) => {
            event.stopPropagation();
            if (!getAIChatOpen(store.getState()))
                return;
            resizing.current = true;
            splitPanelRef.current?.setAttribute("data-resizing", "true");
        };
        medianRef.current?.addEventListener("pointerdown", handlePointerDown);
        return () => {
            medianRef.current?.removeEventListener("pointerdown", handlePointerDown);
        };
    }, []);
    useEffect(() => {
        const handlePointerUp = (event) => {
            event.stopPropagation();
            resizing.current = false;
            splitPanelRef.current?.setAttribute("data-resizing", "false");
        };
        medianRef.current?.addEventListener("pointerup", handlePointerUp);
        return () => {
            medianRef.current?.removeEventListener("pointerup", handlePointerUp);
        };
    }, []);
    useEffect(() => {
        if (splitPanelRef.current === null)
            return;
        const handleMouseMove = (event) => {
            if (resizing.current) {
                const newMedianLeft = event.clientX - medianWidth;
                localStorage.setItem("mapka-leftPanelWidth", newMedianLeft.toString());
                setLeftPanelWidth(newMedianLeft);
            }
        };
        splitPanelRef.current.addEventListener("mousemove", handleMouseMove);
        return () => {
            splitPanelRef.current?.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);
    const rightPanelMarginLeft = leftPanelWidth > 0
        ? "0px"
        : "-16px";
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
        #mapka-split-panel[data-direction=col] {
          display: grid;
          grid-template-columns: ${leftPanelWidth}px ${medianWidthPx} ${rightPanelWidth};
          grid-template-rows: 1fr;
          height: calc(100% - 16px);
          margin: 16px;
          margin-top: 0;
          overflow: hidden;
          min-width: calc(100% - 32px);
          max-width: calc(100% - 32px);
        }

        #mapka-split-panel[data-resizing=true][data-direction=col] {
          cursor: col-resize;
          user-select: none;
        }

        #mapka-split-panel[data-direction=col] #median {
          inline-size: ${medianWidthPx};
          grid-column: 2 / 3;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 120ms ease;
        }
        #mapka-split-panel[data-chat-open=true][data-direction=col] #median::before {
          content: "";
          width: 4px;
          height: 40px;
          border-radius: 2px;
          background-color: var(--mantine-color-gray-4);
          transition: background-color 120ms ease, height 120ms ease;
        }
        #mapka-split-panel[data-chat-open=true][data-direction=col] #median:hover {
          cursor: col-resize;
          background-color: var(--mantine-color-gray-1);
        }
        #mapka-split-panel[data-chat-open=true][data-direction=col] #median:hover::before {
          background-color: var(--mantine-color-gray-6);
          height: 56px;
        }
        #mapka-split-panel[data-resizing=true][data-direction=col] #median::before {
          background-color: var(--mantine-primary-color-filled, var(--mantine-color-blue-6));
          height: 56px;
        }
        #mapka-split-panel[data-direction=col] #left-panel {
          grid-column: 1 / 2;
          grid-row: 1 / 1;
          height: 100%;
          overflow: hidden;
        }
        #mapka-split-panel[data-direction=col] #right-panel {
          grid-column: 3 / 4;
          grid-row: 1 / 1;
          height: 100%;
          overflow: hidden;
          margin-left: ${rightPanelMarginLeft};
        }
        ` }), _jsxs("div", { id: "mapka-split-panel", "data-direction": "col", "data-chat-open": isChatOpen ? "true" : "false", ref: splitPanelRef, children: [_jsx("div", { id: "left-panel", children: leftPanel }), _jsx("div", { id: "median", ref: medianRef }), _jsx("div", { id: "right-panel", children: rightPanel })] })] }));
}
